import React from 'react';

import { tv } from '../../tv';

/**
 * A count the system computed, on a tiny pill: an unread total, a
 * notification count.
 *
 * One of three look-alike components with different owners (see
 * docs/specs/frontend/design-system/badge-status-tag.md): a Badge is what the
 * system counts, a Status is the state it computed, a Tag is what a user
 * attached. A state word belongs on Status, a user label on Tag.
 *
 * The narrowest of the three by design: a count annotates something else (a
 * nav item, a tab) and must not compete with it. It is a circle at one digit
 * and grows into a pill as the count widens, which is why the minimum width
 * equals the height; the height is the trio's 20px md scale, so a count and a
 * small state pill sit level in the same row.
 *
 * Capping the display is the CALLER's job: pass the string to show ("99+"),
 * not a raw count to clamp. The cap is product policy (an inbox caps at 99,
 * an approval queue may not) and baking one number in here would make every
 * surface share it. The contract is four characters at most, INCLUDING the
 * "+" ("999+" is the widest legal display): past that the pill stops
 * reading as an annotation and the caller is rendering data, not a count.
 *
 * The number carries the meaning and the tone only reinforces it (WCAG
 * 1.4.1); there is no unlabeled colored variant.
 */

/* Three fill styles, every colour read from the tone cascade (tokens.md)
 * through data-tone on the root; neutral, chrome rather than a hue, keeps
 * its own three pairings in the compound table. Neutral solid darkens to
 * step 11 in light (gray 9 is 3.3:1 under white) and keeps step 9 in dark. */
const badgeStyles = tv({
  base: [
    'ck:inline-flex ck:items-center ck:justify-center ck:rounded-full ck:border',
    'ck:h-5 ck:min-w-5 ck:px-1 ck:text-xs ck:font-semibold',
  ],
  variants: {
    variant: {
      solid: 'ck:bg-fill ck:border-transparent ck:text-tone-contrast',
      soft: 'ck:bg-tone-bg ck:border-transparent ck:text-tone-text',
      outline: 'ck:bg-transparent ck:border-tone-border ck:text-tone-text',
    },
    tone: { neutral: '', info: '', success: '', warning: '', critical: '', ai: '' },
  },
  compoundVariants: [
    /* The AA deviation; see the anatomy note above. */
    {
      tone: 'neutral',
      variant: 'solid',
      class: 'ck:bg-neutral-11 ck:dark:bg-neutral-9 ck:text-white',
    },
    { tone: 'neutral', variant: 'soft', class: 'ck:bg-neutral-a-3 ck:text-neutral-11' },
    { tone: 'neutral', variant: 'outline', class: 'ck:text-neutral-11' },
  ],
  defaultVariants: { tone: 'neutral', variant: 'soft' },
});

export interface BadgeProps {
  /**
   * What the count means. `neutral` is the default because most counts are
   * plain facts; a tone above it says the counted things need attention.
   */
  tone?: 'neutral' | 'info' | 'success' | 'warning' | 'critical' | 'ai';
  /**
   * The fill style. `soft` (the default): a count annotates something else and
   * reads on the tone's wash, like Status and Tag. `solid` is for the rare
   * count that has to be seen from across the screen.
   */
  variant?: 'solid' | 'soft' | 'outline';
  /**
   * The count to show, already capped by the caller: at most four
   * characters including the "+" ("99+", "999+").
   */
  children: number | string;
  id?: string;
}

export const Badge = React.forwardRef<HTMLSpanElement, BadgeProps>(function Badge(
  { tone = 'neutral', variant = 'soft', children, ...props },
  ref,
) {
  return (
    <span
      {...props}
      ref={ref}
      className={badgeStyles({ tone, variant })}
      data-component="badge"
      data-tone={tone}
    >
      {children}
    </span>
  );
});
